import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";

import MenuIcon from "@mui/icons-material/Menu";
import CancelIcon from "@mui/icons-material/Cancel";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";

import { useWindowScrollPositions } from "../hooks/getScrollPositions";
import { COLORED_LOGO, WHITE_LOGO } from "../utils/constants";

const DRAWER_WIDTH = 300;

const navItems = [
  { id: 1, title: "Home", link: "home" },
  { id: 2, title: "About", link: "about" },
  { id: 3, title: "Remote Sensing", link: "punjab-agri" },
  { id: 4, title: "Media", link: "media" },
  { id: 5, title: "Advisory Service", link: "advisory" },
  { id: 6, title: "Agri Stack", link: "agri" },
  { id: 7, title: "Contact us", link: "contact" },
  {
    id: 8,
    title: "Crop monitoring Agriculture",
    link: "crop-monitoring",
  },
  { id: 9, title: "Careers", link: "careers" },
  { id: 10, title: "Start Crop Monitoring", link: "login" },
];

export default function Header({ home = false, window: windowProp }) {
  const { scrollY } = useWindowScrollPositions();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = React.useState(false);

  const isHomeTop = home && scrollY === 0;

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleLogoClick = () => {
    navigate("/");
    setMobileOpen(false);
  };

  const openLinkedIn = () => {
    window.open(
      "https://www.linkedin.com/company/limscoe/",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const openInstagram = () => {
    window.open(
      "https://instagram.com/lims_coe?igshid=MzMyNGUyNmU2YQ==",
      "_blank",
      "noopener,noreferrer"
    );
  };

  const getNavPath = (link) => `/${link.toLowerCase()}`;

  const container =
    windowProp !== undefined
      ? () => windowProp().document.body
      : undefined;

  const logo = isHomeTop ? WHITE_LOGO : COLORED_LOGO;

  const navTextColor = isHomeTop ? "white" : "black";

  const drawer = (
    <Box sx={{ textAlign: "center", py: 1 }}>
      <Box
        onClick={handleLogoClick}
        sx={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          mb: 1,
        }}
      >
        <img
          src={COLORED_LOGO}
          alt="LIMS"
          style={{
            width: "85px",
            height: "85px",
            objectFit: "contain",
          }}
        />
      </Box>

      <Divider />

      <List>
        {navItems
          .filter((item) => item.title !== "Start Crop Monitoring")
          .map((item) => (
            <ListItem key={item.id} disablePadding>
              <ListItemButton
                component={Link}
                to={getNavPath(item.link)}
                onClick={() => setMobileOpen(false)}
                sx={{
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <ListItemText primary={item.title} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>

      <Divider sx={{ my: 1 }} />

      <Button
        component={Link}
        to="/login"
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setMobileOpen(false)}
        variant="contained"
        sx={{
          width: "85%",
          backgroundColor: "#0e97ff",
          borderRadius: "10px",
          boxShadow: "none",
          color: "#fff",
          fontSize: "13px",
          fontWeight: 400,
          padding: "9px 8px",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#0c86df",
            boxShadow: "none",
          },
        }}
      >
        Start Crop Monitoring
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 2,
        }}
      >
        <IconButton
          onClick={openLinkedIn}
          aria-label="LIMS LinkedIn"
          sx={{ color: "#0e97ff" }}
        >
          <LinkedInIcon />
        </IconButton>

        <IconButton
          onClick={openInstagram}
          aria-label="LIMS Instagram"
          sx={{ color: "#0e97ff" }}
        >
          <InstagramIcon />
        </IconButton>
      </Box>

      <IconButton
        onClick={handleDrawerToggle}
        aria-label="close menu"
        sx={{
          mt: 1,
          backgroundColor: "#0e97ff",
          color: "#fff",
          "&:hover": {
            backgroundColor: "#0c86df",
          },
        }}
      >
        <CancelIcon />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <AppBar
        component="nav"
        position="fixed"
        sx={{
          height: "100px",
          display: "flex",
          justifyContent: "center",
          backgroundColor: isHomeTop ? "transparent" : "white",
          boxShadow: "none",
          transition: "background-color 0.3s ease-in-out",
        }}
      >
        <Toolbar>
          {/* Mobile Menu */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              color: navTextColor,
              display: { xs: "flex", lg: "none" },
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Mobile Logo */}
          <Box
            onClick={handleLogoClick}
            sx={{
              display: { xs: "flex", lg: "none" },
              cursor: "pointer",
            }}
          >
            <img
              src={logo}
              alt="LIMS"
              style={{
                width: "50px",
                height: "50px",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Desktop Logo */}
          <Box
            onClick={handleLogoClick}
            sx={{
              flexGrow: 1,
              display: { xs: "none", lg: "block" },
              cursor: "pointer",
            }}
          >
            <img
              src={logo}
              alt="LIMS"
              style={{
                width: "85px",
                height: "85px",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* Desktop Navigation */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
            }}
          >
            {navItems.map((item) => {
              const isStartMonitoring =
                item.title === "Start Crop Monitoring";

              return (
                <Button
                  key={item.id}
                  component={Link}
                  to={getNavPath(item.link)}
                  {...(isStartMonitoring && {
                    target: "_blank",
                    rel: "noopener noreferrer",
                  })}
                  sx={{
                    color: isStartMonitoring
                      ? "#a19c0c"
                      : navTextColor,
                    px: { lg: 1.25, xl: 1.8 },
                    py: 0.8,
                    fontSize: "15px",
                    fontWeight: 700,
                    textTransform: "none",

                    ...(isStartMonitoring && {
                      backgroundColor: "white",
                      border: isHomeTop
                        ? "none"
                        : "1px solid #a19c0c",
                      borderRadius: "10px",
                      ml: 1,

                      "&:hover": {
                        backgroundColor: "white",
                      },
                    }),
                  }}
                >
                  {item.title}
                </Button>
              );
            })}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <nav>
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
      </nav>
    </Box>
  );
}
